/* eslint-disable no-nested-ternary */
import { Dispatch } from 'redux';
import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  CircularProgress,
  Drawer,
  makeStyles,
  OutlinedInput,
  Paper,
  Theme,
} from '@material-ui/core';
import clsx from 'clsx';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import { Alert } from '@material-ui/lab';

import Cart from './cart';
import DrawerAppBar from './DrawerAppBar';
import OrderPage from './Order';
import ProductBrowser from './ProductBrowser';
import { selectWarehouseAction } from '../actions/warehouse';
import { State } from '../reducers/typings';
import { Order } from '../services/typings';
import { fetchOrders } from '../services/order';
import { ProductAvailabilityChange } from '../services/warehouse';

const drawerWidthFullyOpen = 645;
const drawerWidthClosed = 300;

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    userSelect: 'none',
  },
  content: {
    flexGrow: 1,
  },
  drawer: {
    width: drawerWidthFullyOpen,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerContainer: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.common.white,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(10),
  },
  drawerOpen: {
    width: drawerWidthFullyOpen,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: drawerWidthClosed,
  },
  productSearch: {
    backgroundColor: theme.palette.common.white,
  },
  switchMode: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  ordersContainer: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    height: '100%',
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px ${theme.spacing(0.5)}px`,
  },
  order: {
    margin: `${theme.spacing(0.5)}px`,
    justifyContent: 'left',
    padding: '28px 16px',
    overflow: 'hidden',
    color: 'black',
  },
  editContainer: {
    marginTop: theme.spacing(1),
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px ${theme.spacing(0.5)}px`,
  },
  edit: {
    margin: `${theme.spacing(0.5)}px`,
    justifyContent: 'left',
    overflow: 'hidden',
  },
}));

export interface Props extends RouteComponentProps {
  warehouse: State['warehouse'];
  client: State['client'];
  reloadWarehouse(warehouseId: number, warehouseName: string): void;
}

function mapStateToProps(state: State): Pick<Props, 'warehouse' | 'client'> {
  return {
    warehouse: state.warehouse,
    client: state.client,
  };
}

function mapDispatchToProps(dispatch: Dispatch): Pick<Props, 'reloadWarehouse'> {
  return {
    reloadWarehouse: (warehouseId, warehouseName): void => {
      // @ts-ignore
      dispatch(selectWarehouseAction(warehouseId, warehouseName));
    },
  };
}

const Home: FC<Props> = ({ warehouse, client }: Props): ReactElement => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(true);
  const [snackBarMessage, setSnackBarMessage] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [order, setOrder] = useState<Order | null>(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [productAvailabilityChanges, setProductAvailabilityChanges] = useState<
    ProductAvailabilityChange[]
  >([]);

  const updateOrders = useCallback(async () => {
    if (warehouse.warehouseId <= 0) {
      return;
    }
    setLoadingOrders(true);
    try {
      const response = await fetchOrders(warehouse.warehouseId);
      setOrders(response);
    } catch (error) {
      setSnackBarMessage(error.message || 'Erreur lors du chargement des commandes');
    } finally {
      setLoadingOrders(false);
    }
  }, [warehouse.warehouseId]);

  useEffect(() => {
    updateOrders();
  }, [warehouse.warehouseId]);

  useEffect(() => {
    if (order) {
      if (orders.findIndex(o => o.id === order.id) === -1) {
        if (orders.length > 0) {
          setOrder(orders[0]);
        } else {
          setOrder(null);
        }
      }
    }
  }, [order, orders]);

  const changeProductOrderAvailability = useCallback(
    (product_id: number): void => {
      setProductAvailabilityChanges(oldList => {
        const newList = oldList.filter(e => e.product_id !== product_id);
        const oldElement = oldList.find(e => e.product_id === product_id);
        if (!oldElement) {
          const allProducts = warehouse.currentMenu
            ? warehouse.currentMenu.categories.flatMap(category => category.products)
            : [];
          const oldValue = allProducts.find(p => p.id === Number(product_id))?.available_for_order;
          if (oldValue !== undefined) newList.push({ product_id, available: !oldValue });
        }
        return newList;
      });
    },
    [warehouse],
  );

  return (
    <div className={classes.wrapper}>
      <main className={classes.content}>
        {!order && (
          <ProductBrowser
            expanded={open}
            search={productSearch}
            productAvailabilityChanges={productAvailabilityChanges}
            changeProductOrderAvailability={changeProductOrderAvailability}
          />
        )}
        {order && (
          <OrderPage
            order={order}
            updateOrders={updateOrders}
            setSnackBarMessage={setSnackBarMessage}
          />
        )}

        <Snackbar
          key={snackBarMessage}
          message={snackBarMessage}
          open={!!snackBarMessage}
          autoHideDuration={30000}
          onClose={() => {
            setSnackBarMessage(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        />
        {client && client.hasPendingTransations && (
          <Snackbar
            open
            autoHideDuration={6000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert variant="filled" severity="warning" color="error">
              Le client a des transactions en attente, à régulariser au plus vite
            </Alert>
          </Snackbar>
        )}
      </main>
      <Drawer
        variant="permanent"
        anchor="right"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx(classes.drawerContainer, {
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <DrawerAppBar open={open} setOpen={setOpen} setSnackBarMessage={setSnackBarMessage} />
        {!order && (
          <OutlinedInput
            placeholder="Rechercher un produit"
            margin="dense"
            className={classes.productSearch}
            value={productSearch}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setProductSearch(event.target.value)
            }
          />
        )}
        {!order && (
          <>
            <Cart isCompact={!open} setSnackBarMessage={setSnackBarMessage} />
          </>
        )}
        {order && (
          <Paper className={classes.ordersContainer}>
            {orders.map(o => (
              <Button
                key={`order_${o.id}`}
                variant="contained"
                color={o.id !== order.id ? 'primary' : 'secondary'}
                className={classes.order}
                onClick={() => setOrder(o)}
              >
                #{o.number} ({o.transaction.items.length} produits) -{' '}
                {o.transaction.virtual_username}
              </Button>
            ))}
          </Paper>
        )}

        <div className={classes.switchMode}>
          {!order && (
            <Button
              variant="contained"
              color="secondary"
              className={classes.switchMode}
              disabled={warehouse.warehouseId <= 0 || loadingOrders}
              onClick={() => {
                updateOrders().then(() => {
                  setOrder(orders[0]);
                });
              }}
            >
              {warehouse.warehouseId <= 0 ? (
                'Selectionnez un warehouse'
              ) : loadingOrders ? (
                <CircularProgress size={20} />
              ) : (
                'Préparation'
              )}
            </Button>
          )}
          {order && (
            <Button
              variant="contained"
              color="primary"
              className={classes.switchMode}
              onClick={() => setOrder(null)}
            >
              Caisse
            </Button>
          )}
        </div>

        <DrawerAppBar open={open} setOpen={setOpen} setSnackBarMessage={setSnackBarMessage} />
      </Drawer>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
